import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { IngredientCard } from '../components/shared/IngredientCard';
import { LocationModal } from '../components/modals/LocationModal';
import { SearchIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon } from 'lucide-react';
export function IngredientGlossaryPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const ingredients = [{
    id: 1,
    name: 'Magnesium',
    benefit: 'Supports muscle recovery, heart health, and better sleep quality.',
    category: ['Sleep', 'Joint'],
    lastUpdated: '30 Oct 2025',
    recommendedProducts: [{
      name: 'Magnesium Complex',
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg',
      link: '/product/1'
    }, {
      name: 'Sleep Support',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg',
      link: '/product/2'
    }, {
      name: 'Recovery Formula',
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg',
      link: '/product/3'
    }]
  }, {
    id: 2,
    name: 'Vitamin C',
    benefit: 'Boosts immune system function and supports healthy, glowing skin.',
    category: ['Immunity', 'Skin'],
    lastUpdated: '28 Oct 2025',
    recommendedProducts: [{
      name: 'Vitamin C 1000mg',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1030868_1.png',
      link: '/product/4'
    }, {
      name: 'Immune Boost',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg',
      link: '/product/5'
    }, {
      name: 'Skin Glow',
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg',
      link: '/product/6'
    }]
  }, {
    id: 3,
    name: 'Omega-3',
    benefit: 'Promotes heart health, brain function, and reduces inflammation.',
    category: ['Heart', 'Joint'],
    lastUpdated: '25 Oct 2025',
    recommendedProducts: [{
      name: 'Fish Oil 1000mg',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg',
      link: '/product/7'
    }, {
      name: 'Heart Support',
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg',
      link: '/product/8'
    }, {
      name: 'Brain Boost',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1030868_1.png',
      link: '/product/9'
    }]
  }, {
    id: 4,
    name: 'Collagen',
    benefit: 'Supports skin elasticity, joint health, and hair strength.',
    category: ['Skin', 'Hair', 'Joint'],
    lastUpdated: '22 Oct 2025',
    recommendedProducts: [{
      name: 'Collagen Peptides',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg',
      link: '/product/10'
    }, {
      name: 'Beauty Formula',
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg',
      link: '/product/11'
    }, {
      name: 'Joint Support',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg',
      link: '/product/12'
    }]
  }, {
    id: 5,
    name: 'Vitamin D3',
    benefit: 'Essential for bone health, immune function, and mood regulation.',
    category: ['Immunity', 'Joint'],
    lastUpdated: '20 Oct 2025',
    recommendedProducts: [{
      name: 'Vitamin D3 5000 IU',
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg',
      link: '/product/13'
    }, {
      name: 'Bone Health',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1030868_1.png',
      link: '/product/14'
    }, {
      name: 'Immune Defense',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg',
      link: '/product/15'
    }]
  }, {
    id: 6,
    name: 'Biotin',
    benefit: 'Promotes healthy hair growth, strong nails, and radiant skin.',
    category: ['Hair', 'Skin'],
    lastUpdated: '18 Oct 2025',
    recommendedProducts: [{
      name: 'Biotin 10000 mcg',
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg',
      link: '/product/16'
    }, {
      name: 'Hair Growth',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg',
      link: '/product/17'
    }, {
      name: 'Beauty Complex',
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg',
      link: '/product/18'
    }]
  }, {
    id: 7,
    name: 'Caffeine',
    benefit: 'Boosts energy levels, mental alertness, and physical performance.',
    category: ['Energy'],
    lastUpdated: '15 Oct 2025',
    recommendedProducts: [{
      name: 'Energy Complex',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1030868_1.png',
      link: '/product/19'
    }, {
      name: 'Pre-Workout',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg',
      link: '/product/20'
    }, {
      name: 'Focus Formula',
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg',
      link: '/product/21'
    }]
  }, {
    id: 8,
    name: 'Green Tea Extract',
    benefit: 'Supports weight management, antioxidant protection, and metabolism.',
    category: ['Weight', 'Energy'],
    lastUpdated: '12 Oct 2025',
    recommendedProducts: [{
      name: 'Green Tea 500mg',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg',
      link: '/product/22'
    }, {
      name: 'Fat Burner',
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg',
      link: '/product/23'
    }, {
      name: 'Metabolism Boost',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1030868_1.png',
      link: '/product/24'
    }]
  }, {
    id: 9,
    name: 'Melatonin',
    benefit: 'Regulates sleep-wake cycles and promotes restful, quality sleep.',
    category: ['Sleep'],
    lastUpdated: '10 Oct 2025',
    recommendedProducts: [{
      name: 'Melatonin 5mg',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg',
      link: '/product/25'
    }, {
      name: 'Sleep Aid',
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg',
      link: '/product/26'
    }, {
      name: 'Night Rest',
      image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg',
      link: '/product/27'
    }]
  }];
  const filteredIngredients = ingredients.filter(ingredient => {
    return ingredient.name.toLowerCase().includes(searchQuery.toLowerCase()) || ingredient.benefit.toLowerCase().includes(searchQuery.toLowerCase());
  });
  // Calculate pagination
  const totalPages = Math.ceil(filteredIngredients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentIngredients = filteredIngredients.slice(startIndex, endIndex);
  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <HelmetProvider data-id="element-7227" data-mp="1nkhqssshrbhq">
      <SEO title="Ingredient Glossary" description="Comprehensive guide to supplement ingredients and their benefits. Learn about vitamins, minerals, herbs, and active compounds in health products for informed wellness choices." canonical="/en/ingredient-glossary/" robots="index, follow, max-image-preview:large" hreflangAr="/ingredient-glossary/" hreflangEn="/en/ingredient-glossary/" hreflangDefault="/ingredient-glossary/" ogImage="https://cdn.innovaksa.net/og/ingredient-glossary.jpg" data-id="element-7228" data-mp="1njq30454yi20" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7229" data-mp="3d2k7qaut4t5z">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7230" data-mp="3i1dhptavy21e">
          <AnnouncementBar data-id="element-7231" data-mp="3i0lo2o0umedr" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-7232" data-mp="3i3g7qdc1fv64" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7233" data-mp="3r33cn93z7j3x" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-7234" data-mp="21mvkdidn5s4n">
          <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-7235" data-mp="21oy8txz5bd79">
            <MobileInternalHeader title="Ingredient Glossary" data-id="element-7236" data-mp="1u44ff91ybdyl" />
          </div>
          <h1 className="sr-only" data-id="element-7237" data-mp="1pcck6l7uok24">
            Ingredient Glossary - Complete Guide to Supplement Ingredients
          </h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Ingredient Glossary'
        }]} title="Ingredient Glossary" description="Comprehensive guide to supplement ingredients and their benefits. Learn about vitamins, minerals, herbs, and active compounds in health products." data-id="element-7238" data-mp="1kb6u6v5krawa" />
          <div className="container mx-auto px-4 py-8" data-id="element-7239" data-mp="1z1w3tt3e0hy1">
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8" data-id="element-7240" data-mp="rpnarqatj568">
              <div className="relative" data-id="element-7241" data-mp="nnaaviir61hd">
                <input type="text" placeholder="Search ingredients or benefits…" value={searchQuery} onChange={e => handleSearchChange(e.target.value)} className="w-full px-4 py-3 pl-12 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e2b55] focus:border-transparent shadow-sm" data-id="element-7242" data-mp="nmh9yy6h6675" />
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-7243" data-mp="k3i7cjtl51y0" />
                {searchQuery && <button onClick={() => handleSearchChange('')} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-7244" data-mp="b4wpkqvxtqya">
                    <XIcon className="w-5 h-5" data-id="element-7245" data-mp="1fmzfgknmict" />
                  </button>}
              </div>
            </div>
            {/* Results count */}
            {filteredIngredients.length > 0 && <div className="text-center mb-6" data-id="element-7246" data-mp="3nljqvq8f0140">
                <p className="text-sm text-gray-600" data-id="element-7247" data-mp="34x9c5we5oxk9">
                  Showing {startIndex + 1}-
                  {Math.min(endIndex, filteredIngredients.length)} of{' '}
                  {filteredIngredients.length} ingredients
                </p>
              </div>}
            {/* Ingredient Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-7248" data-mp="38ybbsebzjbmr">
              {currentIngredients.map(ingredient => <IngredientCard key={ingredient.id} ingredient={ingredient} data-id="element-7249" data-mp="3nuajvoz3hbrw" />)}
            </div>
            {/* No Results */}
            {filteredIngredients.length === 0 && <div className="text-center py-12" data-id="element-7250" data-mp="ldvxvkvz2xu">
                <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-7251" data-mp="3soessdrx7tvm" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-7252" data-mp="ah7i86oeqast">
                  No ingredients found
                </h3>
                <p className="text-gray-500 mb-6" data-id="element-7253" data-mp="5jyza3vfq2qi">
                  Try adjusting your search or clear it to see all ingredients.
                </p>
                <button onClick={() => handleSearchChange('')} className="px-6 py-2 bg-[#1e2b55] text-white font-medium rounded-lg hover:bg-blue-900 transition-colors" data-id="element-7254" data-mp="tm3wvw7oqav5">
                  Clear Search
                </button>
              </div>}
            {/* Pagination */}
            {totalPages > 1 && <div className="flex justify-center items-center gap-2 mb-12" data-id="element-7255" data-mp="6nz6zah87oj4">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`p-2 rounded-lg border ${currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7256" data-mp="anzx3sdoto60">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-7257" data-mp="1w0ubjt3gwg7k" />
                </button>
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => handlePageChange(page)} className={`px-4 py-2 rounded-lg font-medium ${currentPage === page ? 'bg-[#1e2b55] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-7258" data-mp="3bkacakuwqjpo">
                    {page}
                  </button>)}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`p-2 rounded-lg border ${currentPage === totalPages ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7259" data-mp="2srpqfndmlevd">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-7260" data-mp="h3zwidr25haw" />
                </button>
              </div>}
            {/* Educational Disclaimer */}
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 shadow-sm" data-id="element-7261" data-mp="2binh5qecaphw">
              <div className="flex items-start" data-id="element-7262" data-mp="2gbzu339np7ut">
                <AlertCircleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" data-id="element-7263" data-mp="21mamap7265lk" />
                <div data-id="element-7264" data-mp="26kc65ccb68y3">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-7265" data-mp="26lx1332jbtwh">
                    Educational Disclaimer
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-7266" data-mp="2u8d8u4mt2v5l">
                    The information provided here is for educational purposes
                    only and does not replace professional medical advice. The
                    use of any supplement or ingredient is at your own
                    responsibility. Innova Pharmacy is not liable for any
                    decisions based on this content. Always consult your
                    healthcare provider before use.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-7267" data-mp="16ei66kv5i26j">
          <Footer data-id="element-7268" data-mp="11edmyn2uytcm" />
        </div>
        <MobileFooterNav data-id="element-7269" data-mp="11dcbwilue6uw" />
        <PharmacistChat data-id="element-7270" data-mp="11fylsow7gyga" />
        <div className="h-16 md:hidden" data-id="element-7271" data-mp="1gbeh0x8iv0e9"></div>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7272" data-mp="1bbs30lkvdg4e" />
      </div>
    </HelmetProvider>;
}